import config from 'config';
import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { GoogleOAuthProvider } from '@react-oauth/google';
import CloseIcon from 'components/Icons/CloseIcon';
import StickyHeader from 'modules/corporateOffer/components/StickyHeader';
import { useCorporateContext } from 'modules/corporateOffer/CorporateContext';
import { selectMonthPeriod } from 'modules/corporateOffer/helpers';
import { AuthResponse } from 'modules/corporateOffer/types';
import { AppRoutes, routes } from 'router/models';
import theme from 'theme/theme';

import ChoosePasscodeStep from './onboarding/ChoosePasscodeStep';
import ForgotPasswordStep from './onboarding/ForgotPasswordStep';
import RecoveryStep from './onboarding/RecoveryStep';
import SetPasscodeStep from './onboarding/SetPasscodeStep';
import SignInStep from './onboarding/SignInStep';
import SignUpStep from './onboarding/SignUpStep';
import VerifyEmailStep from './onboarding/VerifyEmailStep';
import WelcomeStep from './onboarding/WelcomeStep';

enum OnboardingStep {
  Welcome = 'welcome',
  SignIn = 'signin',
  ForgotPasscode = 'forgot-passcode',
  RecoverPasscode = 'recover-passcode',
  ChoosePasscode = 'choose-passcode',

  SignUp = 'signup',
  VerifyEmail = 'verify-email',
  SetPasscode = 'set-passcode',
}

export const CorporateOnboarding = () => {
  const { offerTerm, isValidCode, department } = useCorporateContext();
  const navigate = useNavigate();
  const { account } = useParams<{ account: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [userInfo, setUserInfo] = useState<{
    email: string;
    name: string;
    pin: string;
  }>({
    email: '',
    name: '',
    pin: '',
  });

  const step = searchParams.get('step') || OnboardingStep.Welcome;

  const tokens = useRef<AuthResponse>({ refreshToken: '', token: '' });

  const monthTerm = selectMonthPeriod(offerTerm);

  if (!isValidCode || !account) {
    return null;
  }

  return (
    <GoogleOAuthProvider clientId={config.googleClientId}>
      <Container>
        <StickyHeader
          disabled
          right={
            <CloseButton
              onClick={() => navigate(routes[AppRoutes.Corporate](account))}
            >
              <CloseIcon color={theme.colors.white} />
            </CloseButton>
          }
        />
        <StepWrapper>
          {step === OnboardingStep.Welcome && (
            <WelcomeStep
              onPositive={() => {
                setSearchParams({ step: OnboardingStep.SignIn });
              }}
              onNegative={() => {
                setSearchParams({ step: OnboardingStep.SignUp });
              }}
            />
          )}

          {/* SIGN IN */}
          {step === OnboardingStep.SignIn && (
            <SignInStep
              account={account}
              onForgotPassword={() => {
                setSearchParams({ step: OnboardingStep.ForgotPasscode });
              }}
            />
          )}
          {step === OnboardingStep.ForgotPasscode && (
            <ForgotPasswordStep
              onSuccess={(email) => {
                setSearchParams({
                  step: OnboardingStep.RecoverPasscode,
                  email,
                });
              }}
              onKnowPassword={() => {
                setSearchParams({ step: OnboardingStep.SignIn });
              }}
            />
          )}
          {step === OnboardingStep.RecoverPasscode && (
            <RecoveryStep
              onSuccess={(accessToken, refreshToken, email) => {
                tokens.current = { refreshToken, token: accessToken };
                setSearchParams({ step: OnboardingStep.ChoosePasscode, email });
              }}
            />
          )}
          {step === OnboardingStep.ChoosePasscode && (
            <ChoosePasscodeStep
              token={tokens.current.token}
              account={account}
            />
          )}

          {/* SIGN UP */}
          {step === OnboardingStep.SignUp && (
            <SignUpStep
              account={account}
              monthTerm={monthTerm}
              defaultValues={{ email: userInfo.email, name: userInfo.name }}
              onSuccess={(emailVal, nameVal) => {
                setUserInfo((prev) => ({
                  ...prev,
                  email: emailVal,
                  name: nameVal,
                }));
                setSearchParams({ step: OnboardingStep.VerifyEmail });
              }}
            />
          )}
          {step === OnboardingStep.VerifyEmail && (
            <VerifyEmailStep
              email={userInfo.email}
              onNotReceivedPin={() => {
                setSearchParams({ step: OnboardingStep.SignUp });
              }}
              onSuccess={(pin) => {
                setUserInfo((prev) => ({
                  ...prev,
                  pin,
                }));
                setSearchParams({ step: OnboardingStep.SetPasscode });
              }}
            />
          )}
          {step === OnboardingStep.SetPasscode && (
            <SetPasscodeStep
              account={account}
              email={userInfo.email}
              pin={userInfo.pin}
              department={department?.displayNameBE ?? null}
            />
          )}
        </StepWrapper>
      </Container>
    </GoogleOAuthProvider>
  );
};

const Container = styled.main`
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100dvh;
    display: flex;
    flex-direction: column;
  }
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
`;

const StepWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  padding-top: 90px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex: 1;
    padding-top: 30px;
    padding-bottom: 30px;

    & > section {
      height: 100%;
      padding-left: 20px;
      padding-right: 20px;
    }
  }
`;
